import React, { useCallback, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import block from 'bem-cn'
import { motion } from 'framer-motion'
import { animateScroll } from 'react-scroll'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ReactComponent as ArrowSVG } from 'shared/img/arrow.svg'
import logoImg from 'shared/img/logo.png'
import { ReactComponent as SideMenuCloseArrowSVG } from 'shared/img/navIcons/side-menu-close-arrow.svg'

import LanguagesImg from 'shared/img/navIcons/language.png'
import ProfileImg from 'shared/img/navIcons/profile.png'

import { useDirection } from 'shared/hooks/useDirection'
import useHideOverflow from 'shared/hooks/useHideOverflow'

import { logout } from 'features/auth/actions'
import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import { addNotify } from 'components/Notify'

import './SideMenu.scss'
import { ISideMenuItem, ISideMenuSubItem, sideMenuItems } from './data'

interface ISideMenuProps {
  isOpen: boolean
  onToggleSideMenu: (val?: boolean) => void
}

const bgVariants = {
  open: {
    opacity: 1,
    '-webkit-backdrop-filter': 'blur(5px)',
    backdropFilter: 'blur(5px)',
    transition: { duration: 0.35 },
  },
  closed: {
    opacity: 0,
    transition: { duration: 0.35 },
    backdropFilter: 'blur(5px)',
    '-webkit-backdrop-filter': 'blur(5px)',
  },
}

const wrapperVariants = {
  open: {
    display: 'flex',
    transition: { duration: 0 },
  },
  closed: {
    display: 'none',
    transition: { when: 'afterChildren' },
  },
}

const b = block('side-menu')

export const SideMenu: React.FC<ISideMenuProps> = ({ isOpen, onToggleSideMenu }) => {
  const dispatch = useDispatch()

  const history = useHistory()

  const { pushNewModal } = useModalContext()

  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const localeSlots = useSelector(selectLocaleDict, shallowEqual).slots
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)

  useHideOverflow(isOpen)

  const direction = useDirection()

  const variants = useMemo(
    () => ({
      open: { opacity: 1, x: 0, transition: { duration: 0.35 } },
      closed: {
        opacity: 0,
        x: direction === 'ltr' ? '-100%' : '+100%',
        transition: { duration: 0.35 },
      },
    }),
    [direction],
  )

  const handleClose = useCallback(() => {
    setOpenMenuIndex(null)
    onToggleSideMenu(false)
  }, [onToggleSideMenu])

  const handleLogoutClick = useCallback(() => {
    dispatch(logout())

    handleClose()
  }, [dispatch, handleClose])

  const handleProfileClick = useCallback(() => {
    pushNewModal(MODAL_ROUTE_URLS.PROFILE('tab'))()

    handleClose()
  }, [handleClose, pushNewModal])

  const handleSubItemClick = useCallback(
    (subItem: ISideMenuSubItem) => {
      history.push(subItem.mobileRoute || subItem.route)
      handleClose()
    },
    [handleClose, history],
  )

  const handleItemClick = useCallback(
    (item: ISideMenuItem, index: number) => {
      if (item.authRequired && !isAuth) {
        addNotify(localeSlots.pleaseAuth)
        return
      }

      if (item.subItems?.length) {
        setOpenMenuIndex(prev => (prev === index ? null : index))
        return
      }
      history.push(item.route)
      handleClose()
    },
    [handleClose, history, isAuth, localeSlots.pleaseAuth],
  )

  return (
    <motion.nav
      initial="closed"
      animate={isOpen ? 'open' : 'closed'}
      variants={wrapperVariants}
      className={b('wrapper', { open: isOpen })}>
      <motion.div className={b()} variants={variants}>
        <div className={b('top')}>
          <Link to="/" className={b('logo-link')} aria-label={locale.home} onClick={() => animateScroll.scrollToTop()}>
            <img src={logoImg} className={b('logo')} alt="logo" />
          </Link>
          <button type="button" className={b('close-button')} onClick={handleClose} aria-label={locale.close}>
            <SideMenuCloseArrowSVG />
          </button>
        </div>
        <div className={b('side-menu-group')}>
          {sideMenuItems.map((item, index) => (
            <div key={item.textID}>
              <div className={b('side-menu-item')} onClick={() => handleItemClick(item, index)}>
                <div className={b('side-menu-item-left')}>
                  <span className={b('side-menu-item-text')}>{locale[item.textID]}</span>
                  {item.subItems?.length ? (
                    <ArrowSVG className={b('side-menu-item-chevron', { open: openMenuIndex === index })} />
                  ) : null}
                </div>
                {item.image ? <img src={item.image} alt={item.textID} className={b('side-menu-item-img')} /> : null}
              </div>
              {item.subItems?.length && openMenuIndex === index ? (
                <div className={b('sub-menu')}>
                  {item.subItems.map(subItem => (
                    <div
                      key={subItem.textID}
                      className={b('sub-menu-item')}
                      onClick={() => handleSubItemClick(subItem)}>
                      {subItem.icon ? <img src={subItem.icon} alt="" className={b('sub-menu-item-icon')} /> : null}
                      {locale[subItem.textID]}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}

          {isAuth && (
            <div className={b('side-menu-item')} onClick={handleProfileClick}>
              <span className={b('side-menu-item-text')}>{locale.profile}</span>
              <img src={ProfileImg} alt="Profile" className={b('side-menu-item-img')} />
            </div>
          )}

          <div className={b('divider')} />

          <div className={b('side-menu-item')} onClick={pushNewModal(MODAL_ROUTE_URLS.SET_LANGUAGE())}>
            <span className={b('side-menu-item-text')}>{locale.language}</span>
            <img src={LanguagesImg} alt="Language" className={b('side-menu-item-img')} />
          </div>

          {isAuth && (
            <div className={b('btn-wrapper')}>
              <button onClick={handleLogoutClick} className={b('logout-btn')}>
                {locale.logout}
              </button>
            </div>
          )}
        </div>
      </motion.div>
      <motion.div onClick={handleClose} className={b('close-block', { open: isOpen })} variants={bgVariants} />
    </motion.nav>
  )
}
